import { ForgotPasswordPage } from "PFApp/forgot_password/forgot_password_page";
import NotFoundRoute from "PFApp/routes/not_found_route";
import { EnsureLoggedOut, WithLayout } from "PFApp/routes/route_helpers";
import { SignInPage } from "PFApp/signin/sign_in_page";
import { SignUpPage } from "PFApp/signup/sign_up_page";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useSignOut } from "PFApp/use_session";
import { LoadingDots } from "PFComponents/loading_dots";
import api from "PFCore/api";
import { headerOnly } from "PFCore/base/page_layouts";
import i18n from "PFCore/i18n";
import { useEffect } from "react";
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router";

export const TokenResetRoute = () => {
  const { resetToken } = useParams<{ resetToken: string }>();
  // Store token with 30 minute expiration for security
  window.storage.setItem("reset_password_token", resetToken, { expiresIn: 30 * 60 });
  return <Navigate to="/password_edit" />;
};

export const SignOutRoute = () => {
  const { signOut } = useSignOut();
  useEffect(() => {
    signOut();
  }, []);
  return <LoadingDots circlesEnabled pageViewEnabled />;
};

export const WelcomeRoute = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showGrowl } = useGrowlStoreActions();
  const signInType = searchParams.get("sign_in_type");
  const welcomeToken = searchParams.get("welcome_token");

  useEffect(() => {
    if (!welcomeToken || signInType === "sso") {
      return;
    }

    api({ url: "welcome", apiVersion: 1, params: { welcomeToken } }).then(
      (resp) => {
        // Store token securely with expiration instead of putting it in URL
        window.storage.setItem("reset_password_token", resp.resetPasswordToken, { expiresIn: 30 * 60 });
        // Navigate without token in URL for better security
        navigate("/password_edit");
      },
      () => {
        showGrowl({
          message: i18n.t("welcome.failure"),
          kind: "error",
          onClick: () => navigate("/")
        });
        navigate("/");
      }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!welcomeToken) {
    return <Navigate to="/" replace />;
  }

  // When user clicks link in "Welcome mail" and account uses SSO
  if (signInType === "sso") {
    showGrowl({
      message: i18n.t("welcome.sso"),
      kind: "success"
    });
    return <Navigate to="/" replace />;
  }

  return null;
};

const UsersRoutes = () => (
  <WithLayout layout={{ sidebar: headerOnly }}>
    <Routes>
      <Route path="password/edit" element={<Navigate to="/password_edit" replace />} />
      <Route path="welcome" element={<Navigate to="/welcome" replace />} />
      <Route
        path="signin"
        element={
          <EnsureLoggedOut>
            <SignInPage adminPath={false} />
          </EnsureLoggedOut>
        }
      />
      <Route
        path="signin_admin"
        element={
          <EnsureLoggedOut>
            <SignInPage adminPath={true} />
          </EnsureLoggedOut>
        }
      />
      <Route
        path="reset_password"
        element={
          <EnsureLoggedOut>
            <ForgotPasswordPage />
          </EnsureLoggedOut>
        }
      />
      <Route
        path="signup"
        element={
          <EnsureLoggedOut>
            <SignUpPage />
          </EnsureLoggedOut>
        }
      />
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  </WithLayout>
);

export default UsersRoutes;
